import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNotify } from 'react-admin';
import {
  postTerminalCredsFunc,
  getTerminalsCredList,
  SaveApiResMessage,
} from '@/actions';
import { Input } from '@/components/Input';
import IconButton from '@/ctComponents/IconButton';
import CustomSwitchV2 from '@/components/CostomSwitch/CustomSwitchV2';
import { ReactSVG } from 'react-svg';
import { Icons, TerminalsIcon } from '@/assets';
import Tooltip from '@/components/Tooltip';
import styles from './terminal-credentials.module.css';
import LoadingV2 from '@/components/Loading';
import { decryptPassword } from '@/ctUtlis/decrypt';
import { usePusher } from '@/App';
import { useStickyHeader } from '@/hooks/useStickyHeader';

const TABLE_HEADERS = [
  'Terminal',
  'User Name/Email',
  'Password',
  'Status',
  'Activate',
];

export const TerminalCredentials = () => {
  const notify = useNotify();
  const { channels } = usePusher();

  const { tableRef, theadRef } = useStickyHeader({
    enabled: true,
  });

  const [formData, setFormData] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [userTokenTemporary, setUserTokenTemporary] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchTerminalsData = useCallback(() => {
    setLoading(true);
    getTerminalsCredList()
      .then((res) => {
        setTerminals(res.data?.default_list || []);
        setCredentials(res.data?.user_credentials || []);
        setUserTokenTemporary(res.data?.user_token_temporary || '');
      })
      .catch((err) => {
        notify(err, 'warning');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [notify]);

  useEffect(() => {
    fetchTerminalsData();
  }, [fetchTerminalsData]);

  const processedCredentials = useMemo(() => {
    const credentialsMap = {};

    credentials.forEach((credential) => {
      // Decrypt the password using the user token
      const decryptedPassword = decryptPassword(
        credential.relation_pass,
        userTokenTemporary
      );

      credentialsMap[Number(credential.relation_id)] = {
        relation_id: Number(credential.relation_id),
        relation_user: credential.relation_user,
        relation_pass: decryptedPassword,
        enable: credential.enable,
        checked: credential.checked,
      };
    });

    return credentialsMap;
  }, [credentials, userTokenTemporary]);

  useEffect(() => {
    if (channels?.privateChannel) {
      const handleCredentialUpdate = (data) => {
        const updatedCredential = data.message;

        setCredentials((prevCredentials) => {
          const updated = [...prevCredentials];
          const existingIndex = updated.findIndex(
            (item) => item.id === updatedCredential.id
          );

          const decryptedPassword = decryptPassword(
            updatedCredential.relation_pass,
            userTokenTemporary
          );

          if (existingIndex !== -1) {
            updated[existingIndex] = {
              ...updated[existingIndex],
              checked_admin: updatedCredential.checked_admin,
              checked: updatedCredential.checked,
              enable: updatedCredential.enable,
            };
          } else {
            updated.push({
              id: updatedCredential.id,
              relation_id: updatedCredential.relation_id,
              relation_user: updatedCredential.relation_user,
              relation_pass: decryptedPassword, // Keep encrypted for storage
              checked_admin: updatedCredential.checked_admin,
              checked: updatedCredential.checked,
              enable: updatedCredential.enable,
            });
          }

          return updated;
        });

        // Update form data with decrypted password
        setFormData((prevFormData) => {
          const updated = { ...prevFormData };
          const relationId = parseInt(updatedCredential.relation_id);
          const decryptedPassword = decryptPassword(
            updatedCredential.relation_pass,
            userTokenTemporary
          );

          updated[relationId] = {
            ...updated[relationId],
            relation_id: relationId,
            relation_user: updatedCredential.relation_user,
            relation_pass: decryptedPassword, // Use decrypted password in form
            checked_admin: updatedCredential.checked_admin,
            checked: updatedCredential.checked,
            enable: updatedCredential.enable,
          };

          return updated;
        });
      };

      channels.privateChannel.bind(
        'UserCredentialUpdate',
        handleCredentialUpdate
      );

      return () => {
        channels.privateChannel.unbind('UserCredentialUpdate');
      };
    }
  }, [channels, userTokenTemporary]);

  useEffect(() => {
    setFormData(processedCredentials);
  }, [processedCredentials]);

  const getTerminalCredential = (terminalId) => {
    const numericTerminalId = Number(terminalId);
    return (
      formData[numericTerminalId] || {
        relation_id: numericTerminalId,
        relation_user: '',
        relation_pass: '',
        enable: false,
        checked: 'INIT',
      }
    );
  };

  const hasAnyCredentials = useMemo(() => {
    return Object.values(formData).some(
      (credential) => credential?.relation_user && credential?.relation_pass
    );
  }, [formData]);

  const handleInputChange = (key, value, terminalId) => {
    const numericTerminalId = Number(terminalId);
    setFormData((prev) => ({
      ...prev,
      [numericTerminalId]: {
        ...getTerminalCredential(numericTerminalId),
        [key]: value,
      },
    }));
  };

  const getStatusText = (checked) => {
    switch (checked) {
      case 'CHECKED':
        return 'verified';
      case 'INIT':
        return 'pending';
      default:
        return 'failed';
    }
  };

  const getStatusClass = (checked) => {
    switch (checked) {
      case 'CHECKED':
        return 'bg-success-light text-success';
      case 'INIT':
        return 'bg-gray-100 text-inactive-text';
      default:
        return 'bg-error-light text-error';
    }
  };

  const getStatusIcon = (checked) => {
    switch (checked) {
      case 'CHECKED':
        return Icons.circleCheck;
      case 'INIT':
        return Icons.halfCircle;
      default:
        return Icons.circleX;
    }
  };

  const createPayload = () => ({
    user_credentials: Object.values(formData)
      .filter(Boolean)
      .map((credential) => ({
        ...credential,
      })),
  });

  const handleSave = () => {
    setIsSaving(true);
    postTerminalCredsFunc(createPayload())
      .then((res) => {
        if (!res.errors) {
          // Store encrypted credentials and update user token
          setCredentials(res.data.user_credentials);
          if (res.data.user_token_temporary) {
            setUserTokenTemporary(res.data.user_token_temporary);
          }
          setTerminals(res.data.default_list || []);
          // notify('Credentials updated successfully', 'success');
          SaveApiResMessage({
            title: res?.data?.messageHeader,
            message: res?.message,
            mode: 'success',
          });
        } else {
          Object.values(res.errors).forEach((error) => {
            // notify(error[0], 'warning');
            SaveApiResMessage({
              title: 'Warning',
              message: error[0],
              mode: 'warning',
            });
          });
        }
      })
      .catch((err) => {
        // notify(err, 'warning');
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  if (loading || !terminals.length) return <LoadingV2 />;

  return (
    <div className="pb-6 min-h-screen">
      <div className="mb-6 pt-6 sticky top-0 z-999 bg-white">
        <h1 className="text-xl font-bold text-primary-text m-0">
          Terminal Login Details
        </h1>
        <div className="flex flex-col md:flex-row items-center justify-between">
          <p className="text-secondary-text text-lg">
            Please enter your login information to securely access the websites
            associated with each terminal
          </p>

          <div className="flex gap-3">
            <IconButton
              text="Save Password"
              onClick={handleSave}
              disabled={!hasAnyCredentials || isSaving}
              loading={isSaving}
              style={{
                height: 34,
                borderRadius: 4,
                width: 130,
                backgroundColor:
                  hasAnyCredentials && !isSaving ? '#1FC16B' : '#E5E7EB',
                color: hasAnyCredentials && !isSaving ? 'white' : '#CACFD8',
                cursor:
                  hasAnyCredentials && !isSaving ? 'pointer' : 'not-allowed',
                borderColor:
                  hasAnyCredentials && !isSaving ? '#1FC16B' : '#F5F7FA',
              }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-custom-card overflow-hidden">
        <div style={{ display: 'none' }}>
          <input
            type="text"
            name="prevent_autofill_username"
            autoComplete="off"
          />
          <input
            type="password"
            name="prevent_autofill_password"
            autoComplete="off"
          />
        </div>

        <div className="overflow-x-auto" ref={tableRef}>
          <table
            className="w-full border-collapse"
            style={{ minWidth: '760px' }}>
            <thead
              ref={theadRef}
              className="border border-solid mb-4  bg-white border-border-primary shadow-md rounded-t-md ">
              <tr className="rounded-t-md">
                {TABLE_HEADERS.map((header, index) => (
                  <th
                    key={index}
                    className={`px-4 h-42 font-normal text-center text-xs text-icon-gray relative ${
                      index === TABLE_HEADERS.length - 1 ? '' : ''
                    }`}>
                    {index !== TABLE_HEADERS.length - 1 && (
                      <div className="absolute right-0 top-2 bottom-2 w-px bg-gray-200"></div>
                    )}
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="">
              <tr className="h-4" />
              {terminals.map((terminal) => {
                const credential = getTerminalCredential(terminal.id);

                return (
                  <>
                    <tr
                      key={terminal.id}
                      style={{
                        outline: '1px solid #E5E7EB',
                        outlineOffset: '-1px',
                      }}
                      className=" h-50 rounded-md mb-2">
                      <td className="relative align-middle text-center">
                        <div className="absolute right-0 top-2 bottom-2 w-px bg-gray-100"></div>
                        <a
                          href={terminal.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="no-underline">
                          <div className="flex flex-col items-center justify-center h-full py-1">
                            <img
                              height={23}
                              src={
                                TerminalsIcon[
                                  terminal.slug?.replace(/\s+/g, '')
                                ]
                              }
                              alt={terminal.slug}
                            />
                            <span className="font-medium text-xs text-gray-500">
                              {terminal.slug || terminal.name}
                            </span>
                          </div>
                        </a>
                      </td>

                      <td className="px-4  relative">
                        <div className="absolute right-0 top-2 bottom-2 w-px bg-gray-100"></div>
                        <Input
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          data-form-type="other"
                          style={{
                            height: '34px',
                          }}
                          name={`username-${terminal.id}-${Math.random()}`}
                          id={`username-${terminal.id}-${Math.random()}`}
                          placeholder="Username/Email"
                          value={credential.relation_user || ''}
                          onChange={(e) =>
                            handleInputChange(
                              'relation_user',
                              e.target.value,
                              terminal.id
                            )
                          }
                          type="text"
                        />
                      </td>

                      <td className="px-4  relative">
                        <div className="absolute right-0 top-2 bottom-2 w-px bg-gray-100"></div>
                        <Input
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          data-form-type="other"
                          style={{
                            height: '34px',
                          }}
                          name={`password-${terminal.id}-${Math.random()}`}
                          id={`password-${terminal.id}-${Math.random()}`}
                          placeholder="Password"
                          value={credential.relation_pass || ''}
                          onChange={(e) =>
                            handleInputChange(
                              'relation_pass',
                              e.target.value,
                              terminal.id
                            )
                          }
                          type="password"
                        />
                      </td>

                      <td className="px-4 text-center relative">
                        <div className="absolute right-0 top-2 bottom-2 w-px bg-gray-100"></div>
                        {!(
                          processedCredentials[Number(terminal.id)]
                            ?.relation_user &&
                          processedCredentials[Number(terminal.id)]
                            ?.relation_pass
                        ) ? (
                          '-'
                        ) : credential.checked === 'INIT' &&
                          processedCredentials[Number(terminal.id)] &&
                          processedCredentials[Number(terminal.id)]
                            ?.relation_user &&
                          processedCredentials[Number(terminal.id)]
                            ?.relation_pass ? (
                          <Tooltip
                            tooltipId={`verification-${terminal.id}`}
                            iconId={`verification-${terminal.id}`}
                            arrow
                            content={'Verification in progress. Please wait...'}
                            position={'top'}>
                            <span
                              className={`w-fit  gap-2 h-7 mx-auto px-5 flex items-center justify-between rounded-full text-xs font-medium capitalize ${getStatusClass(
                                credential.checked
                              )}`}>
                              <ReactSVG
                                src={getStatusIcon(credential.checked)}
                                className={`${styles.statusIcon} animate-spin`}
                                beforeInjection={(svg) => {
                                  svg.setAttribute('width', '20');
                                  svg.setAttribute('height', '20');
                                }}
                              />
                              {getStatusText(credential.checked)}
                            </span>
                          </Tooltip>
                        ) : credential.checked === 'INIT' ? (
                          '-'
                        ) : processedCredentials[Number(terminal.id)] &&
                          processedCredentials[Number(terminal.id)]
                            ?.relation_user &&
                          processedCredentials[Number(terminal.id)]
                            ?.relation_pass ? (
                          <span
                            className={`w-fit gap-2 h-7 mx-auto px-5 flex items-center justify-between rounded-full text-xs font-medium capitalize ${getStatusClass(
                              credential.checked
                            )}`}>
                            <ReactSVG
                              src={getStatusIcon(credential.checked)}
                              className={styles.statusIcon}
                              beforeInjection={(svg) => {
                                svg.setAttribute('width', '20');
                                svg.setAttribute('height', '20');
                              }}
                            />
                            {getStatusText(credential.checked)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>

                      <td className="px-4 ">
                        <div className="flex justify-center">
                          <CustomSwitchV2
                            checked={credential.enable || false}
                            disabled={credential.checked !== 'CHECKED'}
                            onChange={(checked) =>
                              handleInputChange('enable', checked, terminal.id)
                            }
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="h-2"></td>
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
